// Unpaused PDP: carousel, colour cards, add-to-cart price, plus the repeated buttons,
// low-stock notes, video reviews and countdown further down the page.
(function () {
  function initGallery(root) {
    const track = root.querySelector('.upx-track');
    const slides = [...root.querySelectorAll('.upx-slide')];
    const thumbs = [...root.querySelectorAll('.upx-thumb')];
    if (!track || !slides.length) return;

    const current = () => Math.round(track.scrollLeft / track.clientWidth);
    const go = (i) => {
      const n = (i + slides.length) % slides.length;
      track.scrollTo({ left: n * track.clientWidth, behavior: 'smooth' });
    };

    root.querySelector('.upx-arrow--prev')?.addEventListener('click', () => go(current() - 1));
    root.querySelector('.upx-arrow--next')?.addEventListener('click', () => go(current() + 1));
    thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));

    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = current();
        thumbs.forEach((t, j) => t.setAttribute('aria-current', String(i === j)));
      });
    }, { passive: true });
  }

  // Everything below the top section follows the colour chosen in the top section's cards.
  function syncPage(input) {
    const available = input.dataset.available === 'true';
    document.querySelectorAll('input[data-upx-sync]').forEach((hidden) => {
      hidden.value = input.value;
      const btn = hidden.form?.querySelector('.upx-atc');
      if (!btn) return;
      btn.disabled = !available;
      const label = btn.querySelector('.upx-atc__label');
      if (label) label.textContent = available ? label.dataset.add : label.dataset.soldOut;
    });
    document.querySelectorAll('[data-upx-stock]').forEach((note) => {
      note.hidden = input.dataset.low !== 'true';
    });
  }

  function initOffers(section) {
    const form = section.querySelector('form.upx-form');
    const button = form?.querySelector('.upx-atc');
    if (!form || !button) return;
    const label = button.querySelector('.upx-atc__label');
    const was = button.querySelector('.upx-atc__price s');
    const now = button.querySelector('.upx-atc__price strong');
    const stock = section.querySelector('.upx-stock');

    const update = () => {
      const input = form.querySelector('input[name="id"]:checked');
      if (!input) return;
      now.textContent = input.dataset.price;
      was.textContent = input.dataset.compare || '';
      was.hidden = !input.dataset.compare;
      const available = input.dataset.available === 'true';
      button.disabled = !available;
      label.textContent = available ? label.dataset.add : label.dataset.soldOut;
      if (stock) stock.hidden = input.dataset.low !== 'true';
      syncPage(input);

      // Keep ?variant= in the URL so a shared link opens on the same colour.
      if (section.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState({}, '', url);
      }
    };

    form.addEventListener('change', update);
    update();
  }

  // Video reviews: the video is only created (and downloaded) on the first tap, then plays with sound.
  // Tap again to pause. Only one plays at a time.
  function initVideos(section) {
    section.querySelectorAll('.upx-vcard__media').forEach((media) => {
      const tpl = media.querySelector('template[data-upx-video]');
      if (!tpl) return;
      const card = media.closest('.upx-vcard');
      let video;
      media.addEventListener('click', () => {
        if (!video) {
          video = tpl.content.querySelector('video').cloneNode(true);
          video.removeAttribute('controls');
          video.playsInline = true;
          video.preload = 'auto';
          const cover = media.querySelector('.upx-vcard__cover');
          if (cover) video.poster = cover.currentSrc || cover.src;
          video.addEventListener('play', () => card.classList.add('is-playing'));
          video.addEventListener('pause', () => card.classList.remove('is-playing'));
          video.addEventListener('ended', () => card.classList.remove('is-playing'));
          if (cover) cover.after(video); else media.prepend(video);
        }
        if (video.paused) {
          document.querySelectorAll('.upx-vcard video').forEach((v) => v !== video && v.pause());
          const played = video.play();
          if (played) played.catch(() => card.classList.remove('is-playing'));
        } else {
          video.pause();
        }
      });
    });
  }

  // Countdown to one fixed end time (same for every visitor). Hides itself when the time is up.
  function initTimer(section) {
    const timer = section.querySelector('[data-upx-timer]');
    if (!timer) return;
    const end = Number(timer.dataset.end) * 1000;
    const parts = { h: timer.querySelector('[data-h]'), m: timer.querySelector('[data-m]'), s: timer.querySelector('[data-s]') };
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const left = Math.floor((end - Date.now()) / 1000);
      if (left <= 0) {
        timer.hidden = true;
        clearInterval(id);
        return;
      }
      parts.h.textContent = pad(Math.floor(left / 3600));
      parts.m.textContent = pad(Math.floor((left % 3600) / 60));
      parts.s.textContent = pad(left % 60);
    };
    const id = setInterval(tick, 1000);
    tick();
  }

  // Long review quotes: clamp to 6 lines with a "Read more" tap. Short ones stay as they are.
  function initClamps(section) {
    const quotes = section.querySelectorAll('[data-upx-clamp]');
    if (!quotes.length) return;
    const measure = () => {
      quotes.forEach((quote) => {
        const btn = quote.nextElementSibling;
        if (btn.getAttribute('aria-expanded') === 'true') return;
        quote.classList.add('is-clamped');
        const cut = quote.scrollHeight > quote.clientHeight + 1;
        quote.classList.toggle('is-clamped', cut);
        btn.hidden = !cut;
      });
    };
    quotes.forEach((quote) => {
      const btn = quote.nextElementSibling;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', open);
        btn.textContent = open ? 'Read less' : 'Read more';
        quote.classList.toggle('is-clamped', !open);
      });
    });
    measure();
    if (document.fonts) document.fonts.ready.then(measure);
    let t;
    window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(measure, 150); });
  }

  // Announcement bar: once its slot reaches the top of the screen, pin the bar there (the slot keeps
  // its height so nothing below jumps). One observer, no scroll handler.
  function initBar(section) {
    const slot = section.querySelector('[data-upx-bar]');
    if (!slot || !('IntersectionObserver' in window)) return;
    const bar = slot.firstElementChild;
    new IntersectionObserver(([entry]) => {
      const stuck = entry.intersectionRatio < 1 && entry.boundingClientRect.top < 0;
      slot.style.height = stuck ? bar.offsetHeight + 'px' : '';
      bar.classList.toggle('is-stuck', stuck);
    }, { threshold: [0, 1] }).observe(slot);
  }

  function init(scope) {
    scope.querySelectorAll('.upx').forEach((section) => {
      if (section.dataset.upxReady) return;
      section.dataset.upxReady = '1';
      initBar(section);
      initGallery(section);
      initOffers(section);
      initVideos(section);
      initTimer(section);
      initClamps(section);
    });
    // A lower section re-rendered in the editor picks up the current colour again.
    const chosen = document.querySelector('form.upx-form input[name="id"]:checked');
    if (chosen) syncPage(chosen);
  }

  init(document);
  // Re-run when a section is edited in the Shopify theme editor.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
